/**
 * One device: the header, the lifecycle banners, the blurred preview, and eight tabs.
 *
 * The shell owns everything the tabs share. It loads the device, polls presence, takes the hub's
 * pushes and fans them out, and re-reads the record after an action - so a tab is one small module
 * whose only outside dependency is `DeviceTabContext`. That is the opposite of the OpenSilver
 * arrangement, where every tab's state lived as fields on one 3,365-line view model.
 *
 * **THE PAGE IS TABBED TO KILL THE SCROLLING**, and hidden tabs stay alive - see `TabStrip` for why
 * that is not a `TabControl`. The header is outside the strip because three of its actions are
 * everyday device actions whose answers arrive minutes later: the tab a technician is on when the
 * agent acks a restart is not the tab they pressed it from, which is why `RestartStatus` and
 * `WakeStatus` are header lines and not Software-tab lines.
 *
 * **Watching is paid for and has to be released.** `WatchDevice` starts the preview stream and puts
 * the agent's live metrics on the 2-second cadence; a page that closed without unwatching would
 * leave a customer's machine encoding JPEGs for nobody.
 */
import { insets, type Container } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import * as hub from '../../api/hub.js';
import { openDeviceConsole, openRemoteConsole } from '../../consoles.js';
import { ApprovalStatus, ManagementChannel, type DeviceDto, type SnmpMonitorDto } from '../../contracts.gen.js';
import { ago, join, localDateTime } from '../../format.js';
import { isWindowsCaption } from '../../hardware.js';

import { confirm } from '../../ui/dialogs.js';
import { dot } from '../../ui/Dot.js';
import { link } from '../../ui/Link.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { TabStrip } from '../../ui/TabStrip.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h1,
  hstack,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  value,
  vstack
} from '../../ui/layout.js';
import type { CrmPage, PageContext } from '../page.js';
import { adoptPanel } from './adopt.js';
import type { DeviceTabContext } from './context.js';
import { eventsTab } from './events.js';
import { filesTab } from './files.js';
import { historyTab } from './history.js';
import { activityCard, lastSeenOf } from './activity.js';
import { overviewTab } from './overview.js';
import { performanceTab } from './performance.js';
import { DevicePreview, PREVIEW_COLUMN } from './preview.js';
import { snmpTab } from './snmp.js';
import { softwareTab } from './software.js';
import { usersTab } from './users.js';
import { servicesTab } from './services.js';
import { policyTab } from './policy.js';
import { phoneTab } from './phone.js';

/** The OpenSilver page's cadence, unchanged. It also picks up lifecycle changes - see `poll`. */
const PRESENCE_POLL_MS = 4_000;

export function deviceDetailPage(): CrmPage {
  let disposed = false;
  let detachSnmp: (() => Promise<void>) | null = null;
  let preview: DevicePreview | null = null;
  /** Set once the watch is really in place, so unmount never releases one that was never taken. */
  let watching = 0;

  return {
    async mount(host: Container, ctx: PageContext) {
      const deviceId = Number.parseInt(ctx.params.id ?? '', 10);
      let device: DeviceDto | null = null;
      let monitor: SnmpMonitorDto | null = null;
      let busy = false;

      // Collapsed while idle and stacked tight: three empty lines under the buttons were a 100px band
      // above the preview.
      const status = new StatusLine({ collapse: true });
      const restartStatus = new StatusLine({ collapse: true });
      const wakeStatus = new StatusLine({ collapse: true });

      const { view: scroller, content } = scrollPage();
      const page = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
      content.controls.add(page);
      host.controls.add(scroller);

      // ---------------------------------------------------------------
      //  Header
      // ---------------------------------------------------------------

      const backLink = link('← Devices', () => ctx.go('/devices'));

      const presenceDot = dot(false, { size: 14 });
      const title = h1('');
      const presenceText = value('', { font: FONT.bold });
      const loggedOnUser = value('', { font: FONT.small, role: 'muted' });
      // In the header rather than only on the Policy tab: a held PC is the first thing anyone opening it must know.
      const lockLine = value('', { font: FONT.bold, role: 'warn' });

      const takeControl = button('Take control', () => void launchRemote(), true);
      const deviceConsole = button('Device console', () => void launchConsole());
      const restartButton = button('Restart', () => void restart());
      const wakeButton = button('Wake', () => void wake());
      const adoptButton = button('Adopt network device', () => void adopt.toggle());
      const archiveButton = button('', () => void archive());
      const deleteButton = button('', () => void remove());

      // Stretched and wrapping rather than hugged to the right: seven buttons are about 670px of a
      // 740px column, so a row that measures to its content leaves the machine's name squeezed into
      // whatever is left - and at phone width the last three would simply be off the canvas, where
      // there is no document scrollbar to reveal them.
      const actions = buttonRow();
      actions.controls.addRange([
        takeControl,
        deviceConsole,
        restartButton,
        wakeButton,
        adoptButton,
        archiveButton,
        deleteButton
      ]);

      // The header is a column, not a row: see `actions`. The identity keeps its own row so the
      // status line sits under the name rather than beside a button.
      // The name fills what the dot leaves and wraps: a server's generated name is longer than a
      // phone is wide.
      const titleRow = splitRow({ spacing: GAP.md, align: 'center' });
      titleRow.controls.add(presenceDot);
      fillWith(titleRow, title);
      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        titleRow,
        stackOf(inlineRow(), presenceText, loggedOnUser),
        lockLine,
        actions
      );

      // ---------------------------------------------------------------
      //  Banners
      // ---------------------------------------------------------------

      const retiredBanner = card({ tint: 'warn', bordered: false, padding: insets(GAP.md, GAP.sm) });
      const retiredText = text('', { role: 'warn', font: FONT.small, wrap: true });
      retiredBanner.controls.add(retiredText);
      retiredBanner.visible = false;

      const removalBanner = card({ tint: 'danger', bordered: false, padding: insets(GAP.md, GAP.sm) });
      const removalText = text('', { role: 'danger', font: FONT.small, wrap: true });
      removalBanner.controls.add(
        stackOf(
          vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
          text('Agent removal', { role: 'danger', font: FONT.bold }),
          removalText,
          text(
            'Restore this device to cancel removal (only works before the agent has uninstalled). If ' +
              'the machine is gone for good and will never confirm, Delete offers a forced permanent ' +
              'delete.',
            { role: 'warn', font: FONT.tiny, wrap: true }
          )
        )
      );
      removalBanner.visible = false;

      const removedBanner = card({ tint: 'ok', bordered: false, padding: insets(GAP.md, GAP.sm) });
      removedBanner.controls.add(
        stackOf(
          vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
          text('Agent removed', { role: 'ok', font: FONT.bold }),
          text(
            'The agent has been removed from this machine. Its history is kept here; use Delete again ' +
              'to remove the record permanently.',
            { role: 'ok', font: FONT.small, wrap: true }
          )
        )
      );
      removedBanner.visible = false;

      const phoneRemovedBanner = card({ tint: 'ok', bordered: false, padding: insets(GAP.md, GAP.sm) });
      phoneRemovedBanner.controls.add(
        stackOf(
          vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
          text('Management removed', { role: 'ok', font: FONT.bold }),
          text(
            'Google no longer manages this phone. Its history is kept here; Delete permanently removes the record.',
            { role: 'ok', font: FONT.small, wrap: true }
          )
        )
      );
      phoneRemovedBanner.visible = false;

      // ---------------------------------------------------------------
      //  Tabs
      // ---------------------------------------------------------------

      const dc: DeviceTabContext = {
        ctx,
        deviceId,
        device: () => device,
        isOnline: () => device?.presence?.isOnline ?? false,
        isWindows: () => isWindowsCaption(device?.os?.caption),
        reload: () => refreshDevice(),
        relayout: () => relayout(page),
        restart: () => restart(),
        status
      };

      const adopt = adoptPanel(dc);
      const activity = activityCard(dc);
      const overview = overviewTab(dc);
      const software = softwareTab(dc);
      const performance = performanceTab(dc);
      const snmp = snmpTab(dc);
      const files = filesTab(dc);
      const events = eventsTab(dc);
      const users = usersTab(dc);
      const services = servicesTab(dc);
      const policy = policyTab(dc);
      const history = historyTab(dc);
      const phone = phoneTab(dc);
      detachSnmp = () => snmp.detach();
      let phoneTabShown = false;

      // Windows-only: a Synology has no registry uninstall list, no WUA and no Windows event log.
      // The SNMP tab exists only while the server joins a monitor to this device.
      const tabHost = vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false });
      const tabs = new TabStrip(tabHost, [
        { key: 'phone', label: 'Phone', panel: phone.panel, visible: false },
        { key: 'overview', label: 'Overview', panel: overview.panel },
        { key: 'software', label: 'Software', panel: software.panel, visible: false },
        { key: 'performance', label: 'Performance', panel: performance.panel },
        { key: 'snmp', label: 'SNMP', panel: snmp.panel, visible: false },
        { key: 'files', label: 'Files', panel: files.panel },
        { key: 'events', label: 'Events', panel: events.panel, visible: false },
        { key: 'users', label: 'Users', panel: users.panel, visible: false },
        { key: 'services', label: 'Services', panel: services.panel, visible: false },
        { key: 'policy', label: 'Policy', panel: policy.panel, visible: false },
        { key: 'history', label: 'History', panel: history.panel }
      ]);

      // The one thing a hidden tab must not do is a live round trip to the machine - so the event
      // log and the file explorer load on their first reveal rather than at page open.
      tabs.onChange(key => {
        if (key === 'events') events.onShow?.();
        else if (key === 'users') users.onShow?.();
        else if (key === 'services') services.onShow?.();
        else if (key === 'policy') policy.onShow?.();
        else if (key === 'files') files.onShow?.();
        relayout(page);
      });

      preview = new DevicePreview(ctx.desktop);
      // The sentence goes under the preview: beside a fixed-width preview it was a one-word-wide
      // column on a phone.
      const previewRow = vstack({ spacing: GAP.sm });
      previewRow.controls.add(preview.panel);
      // Held to the preview's width: beside the Activity card a wrapping sentence would measure as
      // one long line and leave the card nothing.
      previewRow.controls.add(
        text(
          'The preview is deliberately blurred. Take control opens a full-resolution session in its ' +
            'own tab, which follows the user across the desktop, the login screen, and UAC prompts.',
          { role: 'muted', font: FONT.small, wrap: true, width: PREVIEW_COLUMN, autoWidth: false }
        )
      );

      // Beside the preview, and under it once the card would be squeezed below a readable width. A
      // PC without a preview (a Synology, a phone) gives the card the whole row.
      const previewAndActivity = splitRow({ spacing: GAP.lg, align: 'start', stackBelow: 320 });
      previewAndActivity.controls.add(previewRow);
      fillWith(previewAndActivity, activity.panel);

      page.controls.addRange([
        backLink,
        header,
        stackOf(
          vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }),
          restartStatus.label,
          wakeStatus.label,
          status.label
        ),
        retiredBanner,
        removalBanner,
        removedBanner,
        phoneRemovedBanner,
        adopt.panel,
        previewAndActivity,
        tabs.view,
        tabHost
      ]);

      // ---------------------------------------------------------------
      //  Applying the record
      // ---------------------------------------------------------------

      function isArchived(): boolean {
        return device?.archived ?? false;
      }

      function isRejected(): boolean {
        return device?.status === ApprovalStatus.Rejected;
      }

      /** Retired either way: both leave the active lists and both offer Restore. */
      function isRetired(): boolean {
        return isArchived() || isRejected();
      }

      function isPhone(): boolean {
        return device?.channel === ManagementChannel.Android;
      }

      function applyPresence(): void {
        activity.apply();
        if (isPhone()) {
          // A phone holds no connection to be online on. When it last reported is the fact, and a
          // phone that has reported in the last day is as "up" as a phone gets from here.
          const reported = device?.mobile?.lastStatusReportUtc ?? null;
          const recent = reported !== null && Date.now() - Date.parse(reported) < 24 * 3600_000;
          presenceDot.tone = recent ? 'online' : 'offline';
          presenceDot.invalidate();
          presenceText.text = reported ? `Checked in ${localDateTime(reported)}` : 'Not checked in yet';
          loggedOnUser.text = device?.primaryEmployeeName || 'No employee assigned';
          return;
        }
        const online = dc.isOnline();
        presenceDot.tone = online ? 'online' : 'offline';
        presenceDot.invalidate();
        presenceText.text = online ? 'Online' : 'Offline';
        const seen = device ? lastSeenOf(device) : null;
        loggedOnUser.text = online
          ? device?.presence?.loggedOnUser || 'No one signed in'
          : join([
              seen ? `last online ${ago(seen)}` : 'never online',
              device?.lastLoggedOnUser ? `last user ${device.lastLoggedOnUser}` : null
            ]);

        takeControl.enabled = online;
        restartButton.enabled = online;
        // Wake is the mirror image of Restart: it is the one header action that only does anything
        // while the machine is OFF, so it becomes available exactly as the others go dead.
        wakeButton.enabled = !online;

        software.applyPresence();
        files.applyPresence();
        events.applyPresence();
        users.applyPresence();
        services.applyPresence();

        if (!online) {
          // An offline device pushes nothing. The charts keep their history - that is honest data -
          // but the current-value line and the preview are no longer of anything.
          performance.clearLive();
          preview?.clear();
        }
        if (device?.uninstallRequested) applyLifecycle();
      }

      function applyLifecycle(): void {
        // Windows-only actions. Take control needs a desktop to capture; the Synology agent refuses
        // a restart outright. Wake is NOT gated: Wake-on-LAN lives in the NIC's firmware, so a
        // Synology, a Linux box and a printer all answer it exactly as a PC does.
        const windows = dc.isWindows();
        const phoneDevice = isPhone();
        takeControl.visible = windows;
        restartButton.visible = windows;
        // A phone's Wi-Fi address is in its inventory, but nothing on its network will wake it.
        wakeButton.visible = canWake() && !phoneDevice;
        previewRow.visible = windows;
        deviceConsole.visible = !phoneDevice;
        adoptButton.visible = !phoneDevice;
        tabs.setTabVisible('phone', phoneDevice);
        tabs.setTabVisible('performance', !phoneDevice);
        tabs.setTabVisible('files', !phoneDevice);
        if (phoneDevice && !phoneTabShown) {
          phoneTabShown = true;
          tabs.select('phone');
        }

        archiveButton.text = isRetired() ? 'Restore' : 'Archive';
        // Delete does two different things and the button should say which: on an active device it
        // decommissions (archive plus uninstall the agent), which reads as "nothing was deleted"
        // unless the button warned it was only step one.
        deleteButton.text = isArchived() ? 'Delete permanently' : 'Delete';
        // Ending a phone's management is a wipe, chosen on the Phone tab; only a released phone's
        // record may be deleted from here.
        if (phoneDevice) deleteButton.visible = device?.uninstallCompleted ?? false;

        retiredBanner.visible = isRetired();
        retiredText.text = isRejected()
          ? "This device was rejected at approval — it isn't managed. Use Restore to send it back for approval."
          : 'This device is archived — hidden from active device lists. Use Restore to bring it back.';

        const pending = (device?.uninstallRequested ?? false) && !(device?.uninstallCompleted ?? false);
        removalBanner.visible = pending;
        removalText.text = dc.isOnline()
          ? 'Removing the agent from this machine…'
          : 'The agent will be removed the next time this machine comes online.';
        removedBanner.visible = (device?.uninstallCompleted ?? false) && !phoneDevice;
        phoneRemovedBanner.visible = (device?.uninstallCompleted ?? false) && phoneDevice;

        tabs.setTabVisible('software', windows);
        tabs.setTabVisible('events', windows);
        tabs.setTabVisible('users', windows);
        tabs.setTabVisible('services', windows);
        // From the device record, so it shows before the tab - a live round trip - has ever been opened.
        tabs.setAttention('services', windows && (device?.stoppedServices ?? 0) > 0);
        tabs.setTabVisible('policy', windows);
        tabs.setAttention('policy', windows && (device?.policyIssues ?? 0) > 0);
      }

      /**
       * Whether the CRM knows a hardware address for this machine.
       *
       * The only thing a magic packet can be addressed to - so a device that has never reported an
       * inventory hides the button rather than showing one that fails. All-zero and broadcast
       * addresses are refused for the same reason the server refuses them: both appear in
       * inventories for adapters that have none, and neither identifies a machine.
       */
      function canWake(): boolean {
        for (const nic of device?.hardware?.nics ?? []) {
          const hex = (nic.macAddress ?? '').replace(/[^0-9a-fA-F]/g, '').toLowerCase();
          if (hex.length !== 12) continue;
          if (hex === '000000000000' || hex === 'ffffffffffff') continue;
          return true;
        }
        return false;
      }

      function applyDevice(): void {
        title.text = device?.name ?? '';
        const held = device?.lock ?? null;
        lockLine.text = held
          ? `Locked until unlocked${held.appliedUtc ? '' : ' - waiting for the PC to apply it'}. Unlock it on the Policy tab.`
          : '';
        lockLine.visible = held !== null;
        applyPresence();
        applyLifecycle();
        // Every tab was built before the first read resolved, so this is what fills them in - and
        // what carries a later lifecycle change into them.
        for (const tab of [phone, overview, software, performance, snmp, files, events, users, services, policy, history]) {
          tab.applyDevice?.();
        }
        relayout(page);
      }

      // ---------------------------------------------------------------
      //  Actions
      // ---------------------------------------------------------------

      async function launchRemote(): Promise<void> {
        const outcome = await openRemoteConsole(deviceId);
        if (!outcome.ok && !ctx.cancelled()) {
          status.error(outcome.message);
          relayout(page);
        }
      }

      async function launchConsole(): Promise<void> {
        const outcome = await openDeviceConsole(deviceId);
        if (!outcome.ok && !ctx.cancelled()) {
          status.error(outcome.message);
          relayout(page);
        }
      }

      /**
       * Restarts the machine.
       *
       * On success nothing else happens here: the agent acks over the still-open hub (a "restart"
       * kind on the software-outcome channel), and then presence drops. Reporting success before the
       * ack would claim an outcome the CRM has not been told.
       */
      async function restart(): Promise<void> {
        const name = device?.name ?? 'this device';
        const ok = await confirm(ctx.desktop, {
          title: 'Restart device',
          message:
            `Restart ${name} now?\n\nThe restart is forced — unsaved work on the device may be lost. ` +
            'It goes offline within seconds and returns once Windows is back.'
        });
        if (!ok || ctx.cancelled()) return;
        restartStatus.busy(`Restarting ${name}…`);
        relayout(page);
        try {
          await crm.restartDevice(deviceId);
        } catch (error) {
          if (!ctx.cancelled()) restartStatus.error(describeError(error));
        }
        relayout(page);
      }

      /**
       * Sends a magic packet from another of the customer's agents.
       *
       * **Reported as "sent", never as "woken".** Nothing acknowledges a magic packet at any layer,
       * so promising more than was done would make the normal minute of waiting read as a bug.
       */
      async function wake(): Promise<void> {
        if (busy) return;
        const name = device?.name ?? 'this device';
        busy = true;
        wakeButton.enabled = false;
        wakeStatus.busy(`Sending a wake packet to ${name}…`);
        relayout(page);
        try {
          const result = await crm.wakeDevice(deviceId);
          if (ctx.cancelled()) return;
          wakeStatus.set(
            result.success
              ? `${result.detail} ${name} should come back online within a minute or two — this page ` +
                  'refreshes on its own.'
              : (result.error ?? result.detail),
            result.success ? 'ok' : 'error'
          );
        } catch (error) {
          if (!ctx.cancelled()) wakeStatus.error(describeError(error));
        } finally {
          busy = false;
          wakeButton.enabled = !dc.isOnline();
          relayout(page);
        }
      }

      /**
       * Archives, or restores a retired device.
       *
       * Confirmed either way, because archiving is **not just a change of list**: the agent stands
       * down, so the machine stops being monitored and stops answering commands until it is restored.
       * That consequence is invisible from the button, so the prompt spells it out.
       */
      async function archive(): Promise<void> {
        const name = device?.name ?? 'this device';
        const archiving = !isRetired();
        const ok = await confirm(ctx.desktop, {
          title: archiving ? 'Archive device' : 'Restore device',
          message: archiving && isPhone()
            ? `Archive ${name}?\n\nIt leaves the active device lists and the CRM stops following it. ` +
              'Nothing changes on the phone - Google still manages it. Use Remove management on the Phone tab to release or wipe it.'
            : archiving
            ? `Archive ${name}?\n\nIt leaves the active device lists and its agent stands down — no ` +
              'inventory, no live monitoring, no remote control, and it stops accepting commands. ' +
              'Nothing is deleted, and the agent stays installed: restoring the device brings it all back.'
            : `Restore ${name}?\n\nIt returns to active management and its agent resumes collecting ` +
              'and reporting.'
        });
        if (!ok || ctx.cancelled()) return;
        status.busy(archiving ? `Archiving ${name}…` : `Restoring ${name}…`);
        try {
          device = await crm.archiveDevice(deviceId, archiving);
          if (ctx.cancelled()) return;
          status.ok(archiving ? `Archived ${name}.` : `Restored ${name}.`);
          applyDevice();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
        relayout(page);
      }

      /**
       * Decommissions, or purges an already-archived record.
       *
       * The server decides which from the device's own state. The forced purge exists because the
       * ordinary one waits for the agent's removal ack, and a machine that is GONE - scrapped,
       * re-imaged, no longer bootable - will never send it: without the override the record would be
       * permanently undeletable, since restoring and deleting again only re-requests the uninstall.
       */
      async function remove(): Promise<void> {
        const name = device?.name ?? 'this device';
        const purge = isArchived();
        const pending =
          (device?.uninstallRequested ?? false) && !(device?.uninstallCompleted ?? false);
        const force = purge && pending;

        const message = force
          ? `${name} hasn't confirmed that its agent was removed from the machine, so it normally ` +
            "can't be deleted permanently yet.\n\nForce delete it anyway?\n\nUse this when the " +
            'machine is gone for good — decommissioned, re-imaged or no longer bootable. This removes ' +
            'the device and all its history, and cannot be undone. If that machine ever does come ' +
            'back with the agent still installed, it will re-enroll as a new device awaiting approval.'
          : purge
            ? `Permanently delete ${name}?\n\nThis removes the device and all its history for good. ` +
              'This cannot be undone.'
            : `Delete ${name}?\n\nThe agent will be uninstalled from the machine and the device ` +
              'archived — its history stays visible here. If the machine is offline, it uninstalls the ' +
              'next time it connects.\n\nDelete it again afterwards to remove the record for good.';

        const ok = await confirm(ctx.desktop, {
          title: force ? 'Force delete device' : 'Delete device',
          message
        });
        if (!ok || ctx.cancelled()) return;

        status.busy(purge ? `Deleting ${name}…` : `Decommissioning ${name}…`);
        try {
          const result = await crm.deleteDevice(deviceId, force);
          if (ctx.cancelled()) return;

          if (purge) {
            // A purge answers 204 - no body - so success cannot be told from a failed call by the
            // result alone. Ask whether the record is really gone rather than navigating away from a
            // delete that silently did not happen.
            try {
              await crm.getDevice(deviceId);
              status.error(`Could not delete ${name}. Check the connection to the server and try again.`);
              relayout(page);
              return;
            } catch {
              // Gone, which is the answer we wanted.
            }
            ctx.go('/devices', { replace: true });
            return;
          }

          // Decommission keeps the record, now archived, so the page stays put and reflects the new
          // state rather than navigating away from information that is still worth reading.
          if (result) {
            device = result as DeviceDto;
            applyDevice();
            status.ok(`${name} is archived and its agent has been told to remove itself.`);
          } else {
            await refreshDevice();
          }
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
        relayout(page);
      }

      // ---------------------------------------------------------------
      //  Loading
      // ---------------------------------------------------------------

      async function refreshDevice(): Promise<void> {
        try {
          const fresh = await crm.getDevice(deviceId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          device = fresh;
          applyDevice();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      /**
       * Looks for the SNMP monitor the server joins to this device.
       *
       * Joined by NIC MAC, then IP, then unit serial - a computed join rather than a stored link, so
       * archiving or purging either half self-heals on the next read. 404 is the ordinary answer and
       * means the tab does not exist.
       */
      async function refreshMonitor(): Promise<void> {
        try {
          monitor = await crm.findDeviceMonitor(deviceId, ctx.dispose.signal());
        } catch {
          monitor = null;
        }
        if (ctx.cancelled()) return;
        tabs.setTabVisible('snmp', monitor !== null);
        await snmp.apply(monitor);
      }

      /**
       * The presence poll, which is also the lifecycle poll.
       *
       * It re-reads the whole device rather than just presence, because the two facts a technician
       * waits on here are both on the record: the agent acking its own removal ("removal pending"
       * flipping to "removed"), and a maintenance deadline. One read answers all three.
       */
      async function poll(): Promise<void> {
        try {
          const fresh = await crm.getDevice(deviceId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          const before = device;
          device = fresh;
          if (
            !before ||
            before.presence?.isOnline !== fresh.presence?.isOnline ||
            before.presence?.loggedOnUser !== fresh.presence?.loggedOnUser ||
            before.archived !== fresh.archived ||
            before.status !== fresh.status ||
            before.uninstallRequested !== fresh.uninstallRequested ||
            before.uninstallCompleted !== fresh.uninstallCompleted ||
            before.maintenanceUntilUtc !== fresh.maintenanceUntilUtc ||
            before.mobile?.lastStatusReportUtc !== fresh.mobile?.lastStatusReportUtc ||
            before.mobile?.lostModeSinceUtc !== fresh.mobile?.lostModeSinceUtc
          ) {
            applyDevice();
          }
        } catch {
          // A failed poll leaves the last known state alone: a momentary outage is not evidence that
          // the device changed.
        }
      }

      await refreshDevice();
      if (ctx.cancelled()) return;
      await refreshMonitor();
      if (ctx.cancelled()) return;

      ctx.dispose.interval(() => void poll(), PRESENCE_POLL_MS);

      // Live everything, on top of the poll. Failure is not fatal - the page is correct as of its
      // last read either way, and the poll is what corrects it when a push never arrives.
      if (await hub.ensureConnected()) {
        if (ctx.cancelled()) return;

        ctx.dispose.add(
          hub.on('DevicePresenceChanged', presence => {
            if (presence.deviceId !== deviceId || !device) return;
            device = { ...device, presence };
            applyPresence();
            relayout(page);
          })
        );

        ctx.dispose.add(
          hub.on('DesktopThumbnail', frame => {
            if (frame.deviceId !== deviceId) return;
            preview?.push(frame);
          })
        );

        ctx.dispose.add(
          hub.on('LiveMetricsChanged', sample => {
            if (sample.deviceId !== deviceId) return;
            performance.applySample(sample);
          })
        );

        ctx.dispose.add(
          hub.on('LiveMetricsHistoryChanged', history => {
            if (history.deviceId !== deviceId) return;
            performance.applyHistory(history.samples);
          })
        );

        ctx.dispose.add(
          hub.on('SoftwareInventoryChanged', changed => {
            if (changed !== deviceId) return;
            void software.refresh();
            // The stopped-services count rides the same inventory, on the device record.
            void refreshDevice();
          })
        );

        ctx.dispose.add(
          hub.on('SoftwareUninstallCompleted', result => {
            if (result.deviceId !== deviceId) return;
            const line = `${result.target}: ${result.message}`;
            // The restart ack shares this channel but not this line: its request was made from the
            // header and may well have been made from another tab entirely.
            if (result.kind.toLowerCase() === 'restart') {
              restartStatus.set(line, result.success ? 'ok' : 'error');
            } else {
              software.applyOutcome(line);
            }
            relayout(page);
          })
        );

        ctx.dispose.add(
          hub.on('SoftwareInstallChanged', job => {
            if (job.deviceId !== deviceId) return;
            software.applyInstallJob(job);
            tabs.setAttention('software', software.attention());
          })
        );

        ctx.dispose.add(
          hub.on('DeviceHistoryChanged', changed => {
            if (changed !== deviceId) return;
            history.refresh();
          })
        );

        ctx.dispose.add(
          hub.on('DeviceInventoryChanged', changed => {
            if (changed !== deviceId) return;
            void refreshDevice();
          })
        );

        ctx.dispose.add(
          hub.on('MonitorChanged', customerId => {
            // Fires on every poll report for any of the customer's monitors, so the tab tracks the
            // poll without a timer of its own.
            if (device && customerId === device.customerId) void refreshMonitor();
          })
        );

        await hub.watchDevice(deviceId);
        watching = deviceId;
      }

      // The attention dot is recomputed here rather than only on a push, so a device that already
      // has a pending reboot when the page opens shows it without waiting for one.
      tabs.setAttention('software', software.attention());
      relayout(page);
    },

    /**
     * Releases what `Disposables` cannot see.
     *
     * The watch is the important one: it is server-side state, and a page that closed without
     * releasing it would leave the agent streaming previews and sampling every two seconds for
     * nobody. The preview's last GPU texture goes with it.
     */
    unmount() {
      if (disposed) return;
      disposed = true;
      preview?.dispose();
      preview = null;
      void detachSnmp?.();
      detachSnmp = null;
      if (watching !== 0) {
        void hub.unwatchDevice(watching);
        watching = 0;
      }
    }
  };
}
